import React from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { makeStyles } from '@material-ui/core/styles';
import MapPrintDetailedReport from './MapPrintDetailedReport';
import MapPrintSummaryReport from './MapPrintSummaryReport';
import { SUMMARY_PRINT_REPORT_VALUE } from '../reducers/report';
import RouteSheet from './RouteSheet';
import { renderToStaticMarkup } from 'react-dom/server';
import { ServerStyleSheets, ThemeProvider } from '@material-ui/core/styles';
import { PRINT_MODE } from './PrintSummaryReport';
import moment from 'moment';

const useStyles = makeStyles({
  container: {
    height: '50%',
    display: 'flex',
    'align-items': 'stretch',
    '@media (orientation: portrait)': {
      flexDirection: `column`,
    },
  },
  subContainer: {
    width: '100%',
    height: '50%',
  },
  info: {
    //width: '300px',
    flexGrow: 1,
    flexBasis: 0,
    '@media (orientation: portrait)': {
      width: '100%',
    },
  },
  map: {
    'flex-grow': 2,
    '-webkit-flex-grow': 2,
    display: 'flex',
  },
  printButton: {
    opacity: 0.3,
    position: 'absolute',
    left: 10,
    bottom: 10,
    zIndex: 1000,
    '&:hover': {
      opacity: 1,
    },
  },
});

const MapPrintReport = ({
  object = {},
  addressMap,
  loading,
  disableButton = false,
  options,
  printType,
}) => {
  if (!object) {
    throw new Error('Object value must not be set to null or undefined!');
  }
  const classes = useStyles();
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const orientation = isPortrait ? 'horizontal' : 'vertical';
  const driverNameRef = React.useRef('');
  const [index, setIndex] = React.useState(null);

  const printData = object.print;
  const mapData = object.map;
  const { objectName, plateNumber } = object;

  const handleDriverInput = event => {
    driverNameRef.current = event.currentTarget.value;
  };

  const onPrint = React.useCallback(() => {
    const startDate = new Date(object.start).toLocaleString();
    const endDate = new Date(object.end).toLocaleString();
    const {
      totalRest,
      totalWork,
      startDist,
      endDist,
      totalDist,
      overtime,
    } = object.print;

    const sheets = new ServerStyleSheets();
    const html = renderToStaticMarkup(
      sheets.collect(
        <ThemeProvider>
          <RouteSheet
            data={object.print}
            header={{
              objectName: objectName,
              plateNumber: plateNumber,
              startTime: startDate,
              endTime: endDate,
              mileageStart: startDist,
              mileageEnd: endDist,
              totalRestHours: totalRest,
              totalWorkHours: totalWork,
              totalDistance: totalDist,
            }}
            reportType={printType}
            addressMap={addressMap}
            mode={PRINT_MODE}
          />
        </ThemeProvider>,
      ),
    );
    const css = sheets.toString();

    // Open a blank window
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    // Build the full document structure
    printWindow.document.write(`
      <html>
        <head>
          <title>${plateNumber} - ${moment(startDate).format(
      'DD.MM.YYYY HH:mm',
    )} - ${moment(endDate).format('DD.MM.YYYY HH:mm')}</title>
          <style>
            ${css} 
            /* Optional: Add print-specific overrides here */
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          ${html}
          <script>
            // Wait for content to load, then print and close
            window.onload = () => {
              // Listen for the completion of the print action
              window.onafterprint = () => {
                window.close();
              };              
              window.print();
            };
          </script>          
        </body>
      </html>
    `);
    printWindow.document.close();
    // printWindow.print();
  }, [
    addressMap,
    object.end,
    object.print,
    object.start,
    objectName,
    plateNumber,
    printType,
  ]);

  const onSelectItem = point => {
    const { coordinates } = mapData.geometry;
    const selectedIndex = coordinates.findIndex(item => {
      return item[0] === point[0] && item[1] === point[1]
    })
    setIndex(selectedIndex);
  };

  React.useEffect(() => {
    setIndex(null);
  }, [mapData]);
  // const totalHours = printData.days.reduce(
  //   (hours, period) => {
  //     hours.rest += period.restHours;
  //     hours.work += period.workHours;
  //     return hours;
  //   },
  //   { rest: 0, work: 0 },
  // );

  if(printType === SUMMARY_PRINT_REPORT_VALUE){
    return (
      <MapPrintSummaryReport
        orientation={orientation}
        loading={loading}
        object={object}
        handleDriverInput={handleDriverInput}
        mapData={mapData}
        index={index}
        onPrint={onPrint}
        disableButton={disableButton}
        printData={printData}
        addressMap={addressMap}
        onSelectItem={onSelectItem}
        printOptions={options && options.print}
      />
    )
  } else {
    return (
      <MapPrintDetailedReport
        orientation={orientation}
        loading={loading}
        object={object}
        handleDriverInput={handleDriverInput}
        mapData={mapData}
        index={index}
        onPrint={onPrint}
        disableButton={disableButton}
        printData={printData}
        addressMap={addressMap}
        onSelectItem={onSelectItem}
        printOptions={options && options.print}
      />
    )
  }
};

MapPrintReport.propTypes = {
  object: PropTypes.object,
  addressList: PropTypes.array,
  loading: PropTypes.bool,
  disableButton: PropTypes.bool,
  options: PropTypes.object,
};

export default MapPrintReport;
